import { z } from "zod";

export const certificationTypeSchema = z.enum([
  "CRANE_OPERATOR",
  "FORKLIFT",
  "EXCAVATOR",
  "SCISSOR_LIFT",
  "BOBCAT",
  "CDL",
  "SAFETY_TRAINING",
  "CUSTOM",
]);

export const certificationStatusSchema = z.enum(["ACTIVE", "EXPIRED", "PENDING"]);

export const CERTIFICATION_TYPE_LABELS: Record<CertificationType, string> = {
  CRANE_OPERATOR: "Crane Operator",
  FORKLIFT: "Forklift",
  EXCAVATOR: "Excavator",
  SCISSOR_LIFT: "Scissor Lift",
  BOBCAT: "Bobcat",
  CDL: "CDL",
  SAFETY_TRAINING: "Safety Training",
  CUSTOM: "Custom",
};

export const createCertificationSchema = z.object({
  certificationType: certificationTypeSchema,
  certificationName: z.string().trim().min(1, "Certification name is required").max(200),
  certificationCode: z.string().trim().max(64).optional().nullable(),
  issuedAt: z.string().date().optional().nullable(),
  expiresAt: z.string().date().optional().nullable(),
  documentUrl: z.preprocess(
    (val) => (typeof val === "string" && val.trim() === "" ? null : val),
    z.string().url().max(2000).nullable().optional(),
  ),
  status: certificationStatusSchema.optional(),
  notes: z.string().trim().max(5000).optional().nullable(),
});

export const updateCertificationSchema = createCertificationSchema.partial();

export const workerIdParamSchema = z.object({
  workerId: z.string().uuid(),
});

export const workerCertificationSchema = z.object({
  id: z.string().uuid(),
  workerId: z.string().uuid(),
  certificationType: certificationTypeSchema,
  certificationName: z.string(),
  certificationCode: z.string().nullable(),
  issuedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  documentUrl: z.string().nullable(),
  status: certificationStatusSchema,
  notes: z.string().nullable(),
  isExpired: z.boolean(),
  expiresSoon: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const certificationCheckItemSchema = z.object({
  equipmentId: z.string().uuid(),
  usageHours: z.coerce.number().positive().optional(),
  certificationOverrideReason: z.string().trim().min(1).max(500).optional(),
});

export const checkCertificationsSchema = z.object({
  workerId: z.string().uuid(),
  equipmentUsage: z.array(
    z.object({
      equipmentId: z.string().uuid(),
      usageHours: z.coerce.number().positive().optional(),
    }),
  ).min(1),
});

export const certificationWarningSchema = z.object({
  equipmentId: z.string().uuid(),
  equipmentName: z.string(),
  requiredCertificationType: certificationTypeSchema,
  requiredCertificationLabel: z.string(),
  reason: z.enum(["MISSING", "EXPIRED", "PENDING"]),
  message: z.string(),
});

export const certificationCheckResultSchema = z.object({
  warnings: z.array(certificationWarningSchema),
  allClear: z.boolean(),
});

export const dailyEntryEquipmentUsageSchema = z.object({
  equipmentId: z.string().uuid(),
  usageHours: z.coerce.number().positive(),
  certificationOverrideReason: z.string().trim().min(1).max(500).optional(),
});

export const dailyEntrySubmitSchema = z.object({
  siteId: z.string().uuid(),
  date: z.string().date(),
  notes: z.string().max(2000).optional(),
  workerId: z.string().uuid().optional(),
  equipmentUsage: z.array(dailyEntryEquipmentUsageSchema).optional(),
});

export type CertificationType = z.infer<typeof certificationTypeSchema>;
export type CertificationStatus = z.infer<typeof certificationStatusSchema>;
export type CreateCertificationInput = z.infer<typeof createCertificationSchema>;
export type UpdateCertificationInput = z.infer<typeof updateCertificationSchema>;
export type WorkerCertification = z.infer<typeof workerCertificationSchema>;
export type CertificationWarning = z.infer<typeof certificationWarningSchema>;
export type CertificationCheckResult = z.infer<typeof certificationCheckResultSchema>;
export type DailyEntrySubmitInput = z.infer<typeof dailyEntrySubmitSchema>;
